import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { 
  HelpCircle, 
  MessageSquare, 
  FileText, 
  Mail, 
  ExternalLink,
  Search,
  BookOpen,
  Shield,
  Wallet,
  Bot
} from 'lucide-react';

const categories = [
  {
    icon: BookOpen,
    title: 'Getting Started',
    description: 'New to KOJIRU? Start here.',
    articles: ['Creating an account', 'Connecting your wallet', 'Your first loan request'],
    color: 'from-blue-500 to-cyan-500'
  },
  {
    icon: Wallet,
    title: 'Wallet & Payments',
    description: 'Managing your funds.',
    articles: ['Supported wallets', 'Deposit and withdraw', 'Transaction fees'],
    color: 'from-purple-500 to-pink-500'
  },
  {
    icon: Bot,
    title: 'AI Features',
    description: 'Understanding our AI tools.',
    articles: ['Credit analysis', 'AI agent tiers', 'Staking for AI access'],
    color: 'from-orange-500 to-red-500'
  },
  {
    icon: Shield,
    title: 'Security',
    description: 'Keeping your assets safe.',
    articles: ['Smart contract security', 'Best practices', 'Reporting issues'],
    color: 'from-green-500 to-emerald-500'
  }
];

const popularArticles = [
  'How to create a loan request',
  'Understanding interest rates',
  'KOJIRU token staking guide',
  'How to bid on loans',
  'Withdrawing your funds',
  'AI credit score explained'
];

export default function Support() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Help <span className="bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Center</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              Find answers, guides, and support for all things KOJIRU.
            </p>

            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search for help..."
                className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500 text-lg"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${category.color} flex items-center justify-center mb-4`}>
                  <category.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{category.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{category.description}</p>
                <ul className="space-y-2">
                  {category.articles.map((article, j) => (
                    <li key={j}>
                      <a href="#" className="text-sm text-blue-400 hover:underline">
                        {article}
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="py-12 px-6 bg-gradient-to-b from-transparent via-blue-500/5 to-transparent">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold mb-8 text-center">Popular Articles</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {popularArticles.map((article, i) => (
              <motion.a
                key={i}
                href="#"
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-blue-500/30 transition-colors"
              >
                <FileText className="w-5 h-5 text-blue-500" />
                <span className="text-gray-300 hover:text-white transition-colors">{article}</span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold mb-8 text-center">Still Need Help?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center"
            >
              <MessageSquare className="w-10 h-10 mx-auto mb-4 text-blue-500" />
              <h3 className="font-semibold mb-2">Live Chat</h3>
              <p className="text-gray-400 text-sm mb-4">Chat with our support team in real-time.</p>
              <button className="px-6 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors">
                Start Chat
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center"
            >
              <Mail className="w-10 h-10 mx-auto mb-4 text-purple-500" />
              <h3 className="font-semibold mb-2">Email Support</h3>
              <p className="text-gray-400 text-sm mb-4">Get help via email within 24 hours.</p>
              <a href="mailto:wayne@kojiru.com" className="px-6 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors inline-block">
                Send Email
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center"
            >
              <HelpCircle className="w-10 h-10 mx-auto mb-4 text-orange-500" />
              <h3 className="font-semibold mb-2">FAQ</h3>
              <p className="text-gray-400 text-sm mb-4">Browse frequently asked questions.</p>
              <Link to="/faq" className="px-6 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors inline-block">
                View FAQ
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
